import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core';
import StepperButtons from '../acquisition/StepperButtons';
import ActivityIndicator from '../core/ActivityIndicator.jsx';
import * as Map from '../../../common/map';
import { aoiAreaKm2, aoiGeometry, isMultiPolygon, parseKmlArea } from '../../../common/aoi';
import ShapeEditControl from '../map/ShapeEditControl';
import { useShapeVertexEditing } from '../../../hooks/useShapeVertexEditing';
import { Actions as Mepbay } from '../../../store/ducks/mepbay';
import TourGuider from '../tour/TourGuider';
import { useIsMounted, useLocalStorage } from '../../../common/utils';
import HelpButton from '../core/HelpButton';
import { calculateCloudCoverage as SentinelCloudCoverageCache } from '../../../algorithms/satellite/sentinel';
import { get as getSatellite } from '../../../common/satellites';
import { calculateCloudCoverage } from '../../../algorithms/satellite/landsatToa';
import { eeEval } from '../../../algorithms/utils/index.js';

/**
 * @function AOIChooser
 * @description Second step of the MEPBay acquisition wizard. Lets the user draw the Area of
 * Interest (AOI) on a Google Map — or import it from a KML file —, and previews its performance
 * impact based on area.
 * @param {Object} props - Component properties.
 * @param {Function} props.navigate - Step navigation handler, forwarded to StepperButtons.
 * @see {@link handleDrawingFromMap} - stores the drawn AOI.
 * @see {@link handleChoose} - validates and submits the step.
 * **Called by:
 * @see {MepBayPage} - as the second acquisition step.
 * @memberof module:Mepbay
 * @returns {JSX.Element} The AOI chooser UI.
 */
const AOIChooser = ({ navigate, ...extra }) => {
  const dispatch = useDispatch();
  const mapDiv = useRef();
  const mapRef = useRef();

  const [t] = useTranslation();
  const savedCoordinates = useSelector((state) => state.mepbay.coordinates);
  const availableDates = useSelector((state) => state.mepbay.availableDates);
  const [overlay, setOverlay] = useState(null);
  const [coordinates, setCoordinates] = useState(() => savedCoordinates ?? null);
  const [areaOfPolygon, setAreaOfPolygon] = useState(null);

  /**
   * @description Lets the user drag the drawn AOI's existing points, keeping the coordinates and
   * the area warning in sync with each adjustment.
   * @see {@link useShapeVertexEditing}
   * @memberof module:Mepbay
   */
  const aoiEditing = useShapeVertexEditing({
    onChange: (coords) => {
      setCoordinates(coords);
      setAreaOfPolygon(
        window.google.maps.geometry.spherical.computeArea(coords.map(([lng, lat]) => ({ lat, lng }))) /
          SQUARE_METERS_IN_SQUARE_KILOMETER,
      );
    },
  });

  const [isKmlDialogOpen, setKmlDialogOpen] = useState(false);
  const [kmlLoading, setKmlLoading] = useState(false);
  const [aoiLoading, setAoiLoading] = useState(false);
  /**
   * Whether this step is still on screen. Confirming the area dispatches the AOI, which advances the
   * flow and unmounts this component while the cloud-coverage request is still running - so the
   * `finally` that turns the loading state back off can land after it is gone.
   */
  const isMounted = useIsMounted();
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');

  const [geeErrorOpen, setGeeErrorOpen] = useState(false);

  const SQUARE_METERS_IN_SQUARE_KILOMETER = 1000000;

  useEffect(() => {
    mapRef.current = new window.google.maps.Map(mapDiv.current, {
      center: { lat: -26.285757, lng: -48.73506 },
      zoom: 10,
      scaleControl: true,
      streetViewControl: false,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: window.google.maps.ControlPosition.TOP_LEFT,
      },
      fullscreenControl: true,
      styles: [
        {
          featureType: 'poi',
          stylers: [{ visibility: 'off' }],
        },
      ],
    });

    Map.initializeMap(mapRef.current);
    Map.setDrawingControlsVisible(true);
    Map.onRegionDrawn(handleDrawingFromMap);

    if (coordinates) {
      const openRing = Map.toOpenRing(coordinates);
      const overlayFixed = Map.drawOutline(openRing);
      overlayFixed.setMap(mapRef.current);
      setOverlay(overlayFixed);
      setCoordinates(openRing);
      aoiEditing.setShape(isMultiPolygon(openRing) ? null : overlayFixed, 'polygon');
      Map.setDrawingControlsVisible(false);
      setAreaOfPolygon(aoiAreaKm2(openRing));

      Map.fitToCoordinates(openRing);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @function handleDrawingFromMap
   * @description Handles AOI drawing completion from the map's DrawingManager: stores the drawn
   * overlay/coordinates, disables further drawing, and computes the AOI's area (in km²) for the
   * performance warning message.
   * @param {Object} overlay - Google Maps overlay object.
   * @param {Array<Array<Number>>} coordinates - Array of [lng, lat] coordinate pairs.
   * **Called by:
   * @see {AOIChooser} - via Map's onRegionDrawn listener.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const handleDrawingFromMap = (overlay, coordinates, type) => {
    setOverlay(overlay);
    setCoordinates(coordinates);
    aoiEditing.setShape(overlay, type);
    Map.setDrawingControlsVisible(false);

    var areaM = window.google.maps.geometry.spherical.computeArea(
      coordinates.map((coord) => {
        return { lat: coord[1], lng: coord[0] };
      }),
    );

    var areaKm = areaM / SQUARE_METERS_IN_SQUARE_KILOMETER;

    setAreaOfPolygon(areaKm);
  };

  /**
   * @function handleUndo
   * @description Removes the drawn AOI from the map and re-enables the drawing tools.
   * **Called by:
   * @see {AOIChooser} - via the "Undo" button's onClick.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const handleUndo = () => {
    aoiEditing.setShape(null);
    overlay.setMap(null);
    setOverlay(null);
    setCoordinates(null);
    setAreaOfPolygon(null);
    Map.setDrawingControlsVisible(true);
  };

  /**
   * @function saveCloudCoverageValues
   * @description Calculates cloud coverage percentages for all available images in the ROI and
   * caches the date-to-cloud-coverage mapping in session storage.
   * @param {Object} satellite - Satellite object containing missions array with collection names.
   * @param {String} startDate - Start date in ISO format.
   * @param {String} endDate - End date in ISO format.
   * @param {Object} roi - Earth Engine geometry of the region of interest.
   * **Called by:
   * @see {handleChoose}
   * @memberof module:Mepbay
   * @returns {void}
   */
  const saveCloudCoverageValues = async function (satellite, startDate, endDate, roi) {
    const cloudPropertyName = 'roiCloudPercentage';
    const dtPropertyName = 'system:time_start';
    let collectionWithROICloud =
      satellite.name == 'Landsat' ? calculateCloudCoverage(satellite, roi, startDate) : SentinelCloudCoverageCache(roi);

    let imagesWithCloud = await eeEval(collectionWithROICloud);

    let dateCloudMap = imagesWithCloud.features.map(function (el) {
      return {
        dt: el.properties[dtPropertyName],
        cloud: el.properties[cloudPropertyName],
      };
    });

    console.log('Date to cloud pct mapping', dateCloudMap);
    window.sessionStorage.setItem('roiCloudCoveragePerDate', JSON.stringify(dateCloudMap));
  };

  /**
   * @function getSelectedSatellite
   * @description Gets the currently selected satellite (chosen in the first acquisition step)
   * from session storage.
   * **Called by:
   * @see {handleChoose}
   * @memberof module:Mepbay
   * @returns {Object} The selected satellite object.
   */
  const getSelectedSatellite = function () {
    let selectedSatelliteIndex = Number.parseInt(window.sessionStorage.getItem('selectedSatelliteIndex'));
    let satellite = getSatellite(selectedSatelliteIndex);
    return satellite;
  };

  /**
   * @function handleChoose
   * @description Caches per-date cloud coverage for the ROI/satellite, then dispatches the drawn
   * AOI to the acquisition state, opening the GEE error dialog if the Earth Engine geometry
   * construction fails.
   * @see {@link getSelectedSatellite}
   * @see {@link saveCloudCoverageValues}
   * **Called by:
   * @see {AOIChooser} - via StepperButtons' onNext.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const handleChoose = async () => {
    const isSameAOI = JSON.stringify(coordinates) === JSON.stringify(savedCoordinates);
    if (isSameAOI && Array.isArray(availableDates) && availableDates.length > 0) {
      return;
    }
    if (!isSameAOI) {
      window.sessionStorage.removeItem('mepbayFullImageList');
      window.sessionStorage.removeItem('mepbayImageSelection');
    }

    setAoiLoading(true);
    try {
      aoiEditing.stopEditing();
      let satellite = getSelectedSatellite();
      let startYear = satellite.missions[0].startYear;
      let endYear = new Date().getFullYear();

      await saveCloudCoverageValues(
        satellite,
        `${startYear}-01-01`,
        `${endYear}-12-31`,
        aoiGeometry(coordinates),
      );
      try {
        dispatch(Mepbay.setAOI(overlay, coordinates, aoiGeometry(coordinates)));
      } catch (e) {
        setGeeErrorOpen(true);
      }
    } finally {
      if (isMounted.current) setAoiLoading(false);
    }
  };

  const drawn = Boolean(coordinates);

  const steps = [
    {
      selector: '#areachooser',
      content: t('forms.mepbay.1.description'),
    },
    {
      selector: '#importKml',
      content: t('forms.mepbay.1.importLegend'),
    },
  ];

  const [isTourOpen, setIsTourOpen] = useLocalStorage('showROITour', true);

  /**
   * @function readFileData
   * @description Reads the selected KML file, extracts its polygon coordinates, and sets it as
   * the current AOI on the map.
   * 1. Validates file selection.
   * 2. Reads the file as text.
   * 3. Parses the KML `<coordinates>` tag.
   * 4. Converts coordinate strings to numeric [lng, lat] arrays.
   * 5. Draws the polygon on the map and centers the view on it.
   * **Called by:
   * @see {AOIChooser} - via the KML file input's onChange.
   * @memberof module:Mepbay
   * @returns {boolean|void} false if no file was selected; otherwise void.
   */
  const readFileData = function () {
    let fileField = document.getElementById('file_import_kml');

    if (fileField.files.length === 0) {
      openErrorDialog(t('forms.mepbay.1.errorMessageSelectFile'));
      return false;
    }

    const file = fileField.files[0];
    const reader = new FileReader();
    setKmlLoading(true);

    reader.addEventListener(
      'load',
      function () {
        try {
          const fileContent = reader.result;
          if (fileContent.indexOf('<coordinates>') !== -1) {
            const coordinatesArray = parseKmlArea(fileContent);

            if (coordinatesArray) {
              let overlayFixed = Map.drawOutline(coordinatesArray);

              if (!overlayFixed) {
                openErrorDialog(t('forms.mepbay.1.errorMessageDrawingOutline'));
                return;
              }

              overlayFixed.setOptions({
                fillColor: '#00B3A1',
                fillOpacity: 0.2,
                strokeColor: '#003832',
                strokeOpacity: 1,
                strokeWeight: 0.5,
              });

              const openRing = Map.toOpenRing(coordinatesArray);
              setOverlay(overlayFixed);
              setCoordinates(openRing);
              setAreaOfPolygon(aoiAreaKm2(openRing));
              aoiEditing.setShape(isMultiPolygon(openRing) ? null : overlayFixed, 'polygon');

              Map.fitToCoordinates(openRing);
              Map.setDrawingControlsVisible(false);
              closeFilePanel();
              openInfoDialog(t('forms.mepbay.1.infoMessageSuccess'));
            } else {
              openErrorDialog(t('forms.mepbay.1.errorMessageNoValidCoord'));
            }
          } else {
            openErrorDialog(t('forms.mepbay.1.errorMessageUnableInterpretKml'));
          }
        } finally {
          setKmlLoading(false);
        }
      },
      false,
    );

    if (file) {
      reader.readAsText(file);
    } else {
      setKmlLoading(false);
    }
  };

  /**
   * @function handleDisplayImportKML
   * @description Opens the "import KML" dialog.
   * **Called by:
   * @see {AOIChooser} - via the "Import KML" button's onClick.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const handleDisplayImportKML = function () {
    setKmlDialogOpen(true);
  };

  /**
   * @function closeFilePanel
   * @description Closes the "import KML" dialog.
   * **Called by:
   * @see {AOIChooser} - via the dialog's "Cancel" button.
   * @see {readFileData} - after successfully importing the KML.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const closeFilePanel = function () {
    setKmlDialogOpen(false);
  };

  /**
   * @function openErrorDialog
   * @description Opens the generic error dialog with the given message.
   * @param {String} message - Localized error message to display.
   * **Called by:
   * @see {readFileData}
   * @memberof module:Mepbay
   * @returns {void}
   */
  const openErrorDialog = (message) => {
    setDialogMessage(message);
    setErrorDialogOpen(true);
  };

  /**
   * @function openInfoDialog
   * @description Opens the generic info dialog with the given message.
   * @param {String} message - Localized info message to display.
   * **Called by:
   * @see {readFileData}
   * @memberof module:Mepbay
   * @returns {void}
   */
  const openInfoDialog = (message) => {
    setDialogMessage(message);
    setInfoDialogOpen(true);
  };

  /**
   * @function closeErrorDialog
   * @description Closes the generic error dialog.
   * **Called by:
   * @see {AOIChooser} - via the error dialog's "Close" button.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const closeErrorDialog = () => {
    setErrorDialogOpen(false);
  };

  /**
   * @function closeInfoDialog
   * @description Closes the generic info dialog.
   * **Called by:
   * @see {AOIChooser} - via the info dialog's "Close" button.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const closeInfoDialog = () => {
    setInfoDialogOpen(false);
  };

  /**
   * @function handleCloseGeeError
   * @description Closes the Google Earth Engine access error dialog.
   * **Called by:
   * @see {AOIChooser} - via the GEE error dialog's "Close" button.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const handleCloseGeeError = () => {
    setGeeErrorOpen(false);
  };

  return (
    <React.Fragment>
      <Box>
        <Box
          textAlign="center"
          display="flex"
          justifyContent="center"
          marginBottom={areaOfPolygon ? -9 : 0}
          marginTop={areaOfPolygon ? +3 : 0}
          style={{ color: 'white' }}>
          {areaOfPolygon && (
            <React.Fragment>
              {areaOfPolygon > 20 ? (
                <Typography
                  style={{
                    borderRadius: 10,
                    padding: '10px 20px',
                    backgroundColor: '#f02c2c',
                    fontWeight: 800,
                  }}>
                  {t('forms.mepbay.1.performanceMessage')
                    .replace('{number}', areaOfPolygon.toFixed(2))
                    .replace('{level}', t('forms.mepbay.1.slow').toUpperCase())}
                </Typography>
              ) : areaOfPolygon > 10 ? (
                <Typography
                  style={{
                    borderRadius: 10,
                    padding: '10px 20px',
                    backgroundColor: '#ec942c',
                    fontWeight: 800,
                  }}>
                  {t('forms.mepbay.1.performanceMessage')
                    .replace('{number}', areaOfPolygon.toFixed(2))
                    .replace('{level}', t('forms.mepbay.1.moderate').toUpperCase())}
                </Typography>
              ) : (
                <Typography
                  style={{
                    borderRadius: 10,
                    padding: '10px 20px',
                    backgroundColor: '#40a6ce',
                    fontWeight: 800,
                  }}>
                  {t('forms.mepbay.1.performanceMessage')
                    .replace('{number}', areaOfPolygon.toFixed(2))
                    .replace('{level}', t('forms.mepbay.1.fast').toUpperCase())}
                </Typography>
              )}
            </React.Fragment>
          )}
        </Box>

        <Dialog
          open={errorDialogOpen}
          onClose={closeErrorDialog}
          PaperProps={{ style: { padding: 20, borderRadius: 12 } }}>
          <DialogContent style={{ textAlign: 'center' }}>
            <h2 style={{ marginTop: 0 }}>{t('forms.mepbay.1.errorTitle')}</h2>
            <p style={{ fontSize: 18 }}>{dialogMessage}</p>
          </DialogContent>
          <DialogActions style={{ justifyContent: 'center' }}>
            <Button onClick={closeErrorDialog} color="secondary" variant="contained">
              {t('forms.mepbay.1.close')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={infoDialogOpen}
          onClose={closeInfoDialog}
          PaperProps={{ style: { padding: 20, borderRadius: 12 } }}>
          <DialogContent style={{ textAlign: 'center' }}>
            <h2 style={{ marginTop: 0 }}>{t('forms.mepbay.1.infoTitle')}</h2>
            <p style={{ fontSize: 18 }}>{dialogMessage}</p>
          </DialogContent>
          <DialogActions style={{ justifyContent: 'center' }}>
            <Button onClick={closeInfoDialog} color="primary" variant="contained">
              {t('forms.mepbay.1.close')}
            </Button>
          </DialogActions>
        </Dialog>

        <Button
          id="importKml"
          onClick={handleDisplayImportKML}
          sx={{ boxShadow: 12 }}
          variant="outlined"
          style={{
            cursor: 'pointer',
            color: '#26a69a',
            left: 10,
            top: 205,
            backgroundColor: '#FFF',
            zIndex: 99,
          }}>
          {t('forms.mepbay.1.importTitle')}
        </Button>

        <Dialog
          open={isKmlDialogOpen}
          onClose={closeFilePanel}
          aria-labelledby="kml-dialog-title"
          aria-describedby="kml-dialog-description"
          PaperProps={{ style: { padding: 20, borderRadius: 12 } }}>
          <DialogContent>
            <fieldset style={{ borderColor: '#BBB', display: 'block', fontSize: 15 }} id="import_kml_component">
              <legend>{t('forms.mepbay.1.importLegend')}</legend>
              <input id="file_import_kml" accept=".kml" type="file" onChange={readFileData} style={{ fontSize: 15 }} />
            </fieldset>
          </DialogContent>
          <DialogActions style={{ justifyContent: 'right', paddingRight: 28 }}>
            <Button onClick={closeFilePanel} color="secondary" variant="contained">
              {t('forms.map.cancel')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={geeErrorOpen}
          onClose={handleCloseGeeError}
          aria-labelledby="gee-error-title"
          aria-describedby="gee-error-description"
          PaperProps={{
            style: { padding: 20, borderRadius: 12, textAlign: 'center' },
          }}>
          <DialogTitle id="gee-error-title" style={{ textAlign: 'center' }}>
            {t('forms.mepbay.1.geeError.title')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="gee-error-description" style={{ fontSize: '16px' }}>
              {t('forms.mepbay.1.geeError.content')}
              <a href="https://earthengine.google.com/signup/" target="_blank" rel="noopener noreferrer">
                https://earthengine.google.com/signup/
              </a>
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ justifyContent: 'center' }}>
            <Button onClick={handleCloseGeeError} color="primary" variant="outlined">
              {t('forms.mepbay.1.geeError.close')}
            </Button>
          </DialogActions>
        </Dialog>
        <HelpButton
          onClickFunction={() => {
            setIsTourOpen(true);
          }}
        />

        <div id="areachooser" style={{ width: '100%', height: 600 }}>
          <div id="google-maps" ref={(r) => (mapDiv.current = r)} style={{ height: '100%' }}></div>
        </div>
        <ShapeEditControl editing={aoiEditing} visible={drawn} />
        <StepperButtons
          navigate={navigate}
          nextDisabled={drawn === false || aoiEditing.geometryError !== ''}
          onNext={handleChoose}>
          <Button onClick={handleUndo} disabled={drawn === false} color="secondary" variant="outlined">
            {t('forms.mepbay.1.undo')}
          </Button>
        </StepperButtons>
        <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
      </Box>
      {(kmlLoading || aoiLoading) && (
        <ActivityIndicator
          forceShow={true}
          title={kmlLoading ? t('forms.core.loading.kml') : t('forms.core.loading.cloudCoverage')}
        />
      )}
    </React.Fragment>
  );
};

export default AOIChooser;
